<template>
  <b-card-code title="Overview" sub-title="MikroTik vs Billing consistency snapshot" class="consistency-card">
    <template #header-actions>
      <div class="consistency-header-actions">
        <div class="consistency-checked" v-if="checkedAt">
          Checked: {{ checkedAt }}
        </div>
      <b-button variant="outline-primary" class="mr-1" @click="exportCsv">
        <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
        Export CSV
      </b-button>
      <b-button variant="primary" @click="fetchSummary({ force: true })">
        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
        Refresh
      </b-button>
      </div>
    </template>

    <div class="row mb-1">
      <div
        v-for="item in summaryCards"
        :key="item.key"
        class="col-xl-2 col-lg-3 col-md-4 col-sm-6 mb-1"
      >
        <div class="d-flex align-items-center consistency-kpi">
          <b-avatar size="36" :variant="item.color" class="mr-1">
            <feather-icon :icon="item.icon" size="18" />
          </b-avatar>
          <div>
            <div class="font-weight-bolder">{{ summary[item.key] || 0 }}</div>
            <small class="text-muted">{{ item.label }}</small>
          </div>
        </div>
      </div>
    </div>

    <b-alert
      v-if="routerErrors.length"
      show
      variant="warning"
      class="mb-2"
    >
      <div class="font-weight-bolder mb-50">Router connection issues</div>
      <div v-for="err in routerErrors" :key="`router-error-${err.router_id}`">
        {{ err.router_name }}: {{ err.message }}
      </div>
    </b-alert>

    <div class="border rounded mb-1 consistency-filter">
      <div class="row align-items-end">
        <div class="col-12 col-md-3">
          <b-form-group label="Router" label-class="text-muted small">
            <b-form-select v-model="filters.router" :options="routerOptions" @change="handleRouterChange" />
          </b-form-group>
        </div>
        <div class="col-12 col-md-3">
          <b-form-group label="Issue Type" label-class="text-muted small">
            <b-form-select v-model="filters.issue" :options="issueOptions" />
          </b-form-group>
        </div>
        <div class="col-12 col-md-6">
          <b-form-group label="Search" label-class="text-muted small">
            <b-input-group>
              <b-form-input
                v-model="searchTerm"
                placeholder="Search username, router, profile..."
              />
              <b-input-group-append>
                <b-button variant="primary" class="consistency-clear-btn" @click="resetFilters">
                  Clear
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>
      </div>
      <div class="d-flex flex-wrap align-items-center mt-25">
        <small class="text-muted mr-1">Quick filters:</small>
        <b-button
          size="sm"
          class="mr-50 mb-50"
          :variant="filters.issue === 'All' ? 'primary' : 'outline-primary'"
          @click="setIssueFilter('All')"
        >
          All ({{ summary.total_issues || 0 }})
        </b-button>
        <b-button
          v-for="item in issueQuickFilters"
          :key="item.key"
          size="sm"
          class="mr-50 mb-50"
          :variant="filters.issue === item.key ? 'primary' : 'outline-primary'"
          @click="setIssueFilter(item.key)"
        >
          {{ item.label }} ({{ summary[item.key] || 0 }})
        </b-button>
      </div>
    </div>

    <div class="d-flex justify-content-end align-items-center mb-1 consistency-top-pagination">
      <small class="text-muted mr-75">
        Showing {{ pageStart }}-{{ pageEnd }} of {{ filteredItems.length }}
      </small>
      <b-form-select
        v-model="pageLength"
        :options="[300, 500, 1000, 2000]"
        size="sm"
        class="consistency-page-select"
      />
    </div>

    <vue-good-table
      ref="consistencyTable"
      v-if="filteredItems.length"
      :columns="columns"
      :rows="filteredItems"
      :rowStyleClass="rowClass"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
      }"
      :rtl="direction"
      @on-row-click="onRowClick"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'issue_label'">
          <b-badge :variant="issueVariant(props.row.type)">
            {{ props.row.issue_label }}
          </b-badge>
        </span>
        <span v-else-if="props.column.field === 'status_label'" class="text-nowrap">
          {{ props.row.status_label || '-' }}
        </span>
        <span v-else-if="props.column.field === 'user_name'" class="text-nowrap">
          <b-link
            v-if="props.row.isp_user_name_id"
            :href="userEditLink(props.row)"
            target="_blank"
            rel="noopener"
            class="font-weight-bolder consistency-user-link"
          >
            {{ props.row.user_name }}
          </b-link>
          <span v-else>
            {{ props.row.user_name }}
          </span>
        </span>
        <span v-else-if="props.column.field === 'profile_label'">
          {{ props.row.profile_label || '-' }}
        </span>
        <span v-else-if="props.column.field === 'actions'">
          <div class="consistency-row-actions">
            <template v-for="action in rowActions(props.row)">
              <div
                v-if="action.type === 'select'"
                :key="`${props.row.user_name}-${action.key}`"
                class="action-item"
              >
                <span v-if="action.labelText" class="action-label">{{ action.labelText }}</span>
                <b-form-select
                  :value="action.value"
                  :options="action.options"
                  size="sm"
                  :class="['action-select', action.selectClass]"
                  :disabled="action.disabled || !action.options || !action.options.length"
                  v-b-tooltip.hover
                  :title="action.tooltip"
                  @input="value => handleRowAction(action.key, props.row, value)"
                />
              </div>
              <div
                v-else
                :key="`${props.row.user_name}-${action.key}`"
                class="action-item"
              >
                <span v-if="action.labelText" class="action-label">{{ action.labelText }}</span>
                <b-button
                  size="sm"
                  :variant="action.variant"
                  class="action-pill"
                  :disabled="action.disabled"
                  v-b-tooltip.hover
                  :title="action.tooltip"
                  @click.stop="handleRowAction(action.key, props.row)"
                >
                  {{ action.label }}
                </b-button>
              </div>
            </template>
          </div>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom" slot-scope="props">
        <div class="d-flex justify-content-end flex-wrap">
          <b-pagination
            :per-page="pageLength"
            :total-rows="props.total"
            :value="currentPage"
            align="right"
            class="mt-1 mb-0"
            first-number
            last-number
            next-class="next-item"
            prev-class="prev-item"
            @input="(value)=>handlePageChange(value, props)"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </template>
    </vue-good-table>

    <div v-else-if="isLoading" class="consistency-skeleton">
      <div class="consistency-skeleton-row" v-for="n in 6" :key="`consistency-skeleton-${n}`">
        <div class="consistency-skeleton-block w-25"></div>
        <div class="consistency-skeleton-block w-35"></div>
        <div class="consistency-skeleton-block w-20"></div>
        <div class="consistency-skeleton-block w-30"></div>
      </div>
    </div>

    <div v-else class="text-center text-muted py-2">
      No inconsistencies found for the selected filters.
    </div>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BAvatar,
  BBadge,
  BAlert,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BDropdown,
  BDropdownItem,
  BLink,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store'
import {
  CONSISTENCY_SUMMARY,
  ISP_PACKAGE_LIST,
  PACKAGE_PROFILE_MAPS,
  USERNAME_DELETE,
  USERNAME_UPDATE_PPP_PROFILE,
  USERNAME_TOGGLE_PPP,
  USERNAME_VIEW,
  USERNAME_UPDATE,
} from '@core/services/api'
import Mixin from '@core/services/mixin'
import { checkRes } from '@core/services/helper'
import ToastificationContent from '@core/components/toastification/ToastificationContent'

export default {
  mixins: [Mixin],
  components: {
    BCardCode,
    BAvatar,
    BBadge,
    BAlert,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    BLink,
    VueGoodTable,
  },
  data() {
    return {
      isLoading: false,
      summary: {
        missing_in_router: 0,
        missing_in_billing: 0,
        router_mismatch: 0,
        status_mismatch: 0,
        profile_mismatch: 0,
        total_issues: 0,
      },
      items: [],
      packageOptions: [],
      profilesByRouter: {},
      profilesLoading: {},
      routerErrors: [],
      checkedAt: null,
      mappingMode: 'global',
      pageLength: 300,
      currentPage: 1,
      searchTerm: '',
      selectedRow: null,
      filters: {
        router: 'All',
        issue: 'All',
      },
      routerOptions: [{ value: 'All', text: 'All Routers' }],
      columns: [
        { label: 'Issue', field: 'issue_label', thClass: 'text-nowrap' },
        { label: 'Username', field: 'user_name', thClass: 'text-nowrap' },
        { label: 'Router', field: 'router_label', thClass: 'text-nowrap' },
        { label: 'Actual Router', field: 'actual_router_label', thClass: 'text-nowrap' },
        { label: 'Status', field: 'status_label', thClass: 'text-nowrap' },
        { label: 'Profile', field: 'profile_label', thClass: 'text-nowrap' },
        { label: 'Package', field: 'package_name', thClass: 'text-nowrap' },
        { label: 'Actions', field: 'actions', thClass: 'text-nowrap' },
      ],
      dir: false,
    }
  },
  computed: {
    summaryCards() {
      return [
        {
          key: 'missing_in_router',
          label: 'Missing in Router',
          icon: 'AlertTriangleIcon',
          color: 'light-danger',
        },
        {
          key: 'missing_in_billing',
          label: 'Missing in Billing',
          icon: 'AlertCircleIcon',
          color: 'light-warning',
        },
        {
          key: 'router_mismatch',
          label: 'Router Mismatch',
          icon: 'ShuffleIcon',
          color: 'light-primary',
        },
        {
          key: 'status_mismatch',
          label: 'Status Mismatch',
          icon: 'SlashIcon',
          color: 'light-info',
        },
        {
          key: 'profile_mismatch',
          label: 'Profile Mismatch',
          icon: 'TargetIcon',
          color: 'light-secondary',
        },
      ]
    },
    issueOptions() {
      return [
        { value: 'All', text: 'All Issues' },
        { value: 'missing_in_router', text: 'Missing in Router' },
        { value: 'missing_in_billing', text: 'Missing in Billing' },
        { value: 'router_mismatch', text: 'Router Mismatch' },
        { value: 'status_mismatch', text: 'Status Mismatch' },
        { value: 'profile_mismatch', text: 'Profile Mismatch' },
      ]
    },
    issueQuickFilters() {
      return [
        { key: 'missing_in_router', label: 'Missing in Router' },
        { key: 'missing_in_billing', label: 'Missing in Billing' },
        { key: 'router_mismatch', label: 'Router Mismatch' },
        { key: 'status_mismatch', label: 'Status Mismatch' },
        { key: 'profile_mismatch', label: 'Profile Mismatch' },
      ]
    },
    routerSelectOptions() {
      return this.routerOptions.filter(option => option.value !== 'All')
    },
    packageSelectOptions() {
      return (this.packageOptions || [])
        .filter(pkg => pkg && pkg.id !== undefined && pkg.id !== null)
        .map(pkg => ({
          value: pkg.id,
          text: pkg.package_name || `Package ${pkg.id}`,
          price: pkg.price,
        }))
    },
    pageStart() {
      const total = this.filteredItems.length
      if (!total) return 0
      return (this.currentPage - 1) * this.pageLength + 1
    },
    pageEnd() {
      const total = this.filteredItems.length
      if (!total) return 0
      return Math.min(total, this.currentPage * this.pageLength)
    },
    filteredItems() {
      const routerFilter = this.filters.router
      const issueFilter = this.filters.issue
      const term = (this.searchTerm || '').toLowerCase()

      return this.items.filter(row => {
        const routerId = row.router_id || row.billing_router_id
        const routerOk = routerFilter === 'All' || String(routerId) === String(routerFilter)
        const issueOk = issueFilter === 'All' || row.type === issueFilter
        const searchOk = !term || [
          row.user_name,
          row.router_label,
          row.actual_router_label,
          row.package_name,
          row.profile_label,
        ].some(value => (value || '').toString().toLowerCase().includes(term))

        return routerOk && issueOk && searchOk
      })
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  mounted() {
    this.applyRouteFilters()
    this.fetchSummary()
    this.fetchPackages()
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteFilters()
        this.fetchSummary()
      },
      deep: true,
    },
    searchTerm() {
      this.resetPage()
      this.clearSelection()
    },
    filters: {
      handler() {
        this.resetPage()
        this.clearSelection()
      },
      deep: true,
    },
    pageLength() {
      this.resetPage()
    },
  },
  methods: {
    issueLabel(type) {
      const map = {
        missing_in_router: 'Missing in Router',
        missing_in_billing: 'Missing in Billing',
        router_mismatch: 'Router Mismatch',
        status_mismatch: 'Status Mismatch',
        profile_mismatch: 'Profile Mismatch',
      }
      return map[type] || 'Unknown'
    },
    issueVariant(type) {
      const map = {
        missing_in_router: 'light-danger',
        missing_in_billing: 'light-warning',
        router_mismatch: 'light-primary',
        status_mismatch: 'light-info',
        profile_mismatch: 'light-secondary',
      }
      return map[type] || 'light-secondary'
    },
    decorateItems(items) {
      return (items || []).map(item => {
        const billingStatusLabel = item.billing_status === 1 ? 'Active' : item.billing_status === 0 ? 'Inactive' : '-'
        const routerStatusLabel = item.router_disabled === true ? 'Disabled' : item.router_disabled === false ? 'Enabled' : '-'
        const statusLabel = item.type === 'status_mismatch'
          ? `${billingStatusLabel} / ${routerStatusLabel}`
          : (item.billing_status !== undefined ? billingStatusLabel : routerStatusLabel)

        const expectedProfile = item.expected_profile || item.meta_profile || '-'
        const routerProfile = item.router_profile || '-'
        const profileLabel = item.type === 'profile_mismatch'
          ? `${expectedProfile} -> ${routerProfile}`
          : (routerProfile !== '-' ? routerProfile : expectedProfile)

        const routerLabel = item.billing_router_name || item.router_name || '-'
        let actualRouterLabel = '-'
        if (Array.isArray(item.actual_router_names) && item.actual_router_names.length) {
          actualRouterLabel = item.actual_router_names.join(', ')
        } else if (item.actual_router_name) {
          actualRouterLabel = item.actual_router_name
        } else if (item.router_name && item.type === 'missing_in_billing') {
          actualRouterLabel = item.router_name
        }

        return {
          ...item,
          issue_label: this.issueLabel(item.type),
          router_label: routerLabel,
          actual_router_label: actualRouterLabel,
          status_label: statusLabel,
          profile_label: profileLabel,
          package_name: item.package_name || '-',
        }
      })
    },
    userEditLink(row) {
      if (!row || !row.isp_user_name_id) return '#'
      return this.$router.resolve({
        name: 'isp-user-name-edit',
        params: { usernameId: row.isp_user_name_id },
      }).href
    },
    applyRouteFilters() {
      const issue = this.$route.query.issue
      const routerId = this.$route.query.router_id
      const validIssues = this.issueOptions.map(option => option.value)

      if (issue && validIssues.includes(issue)) {
        this.filters.issue = issue
      } else {
        this.filters.issue = 'All'
      }

      if (routerId) {
        this.filters.router = String(routerId)
      } else {
        this.filters.router = 'All'
      }
    },
    setIssueFilter(value) {
      this.filters.issue = value
      this.syncQuery()
    },
    copyText(text) {
      if (!text) return
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
      }
    },
    getIspId() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      return userData && userData.isp_id ? userData.isp_id : null
    },
    fetchPackages() {
      const ispId = this.getIspId()
      const baseUrl = process.env.VUE_APP_BASEURL + ISP_PACKAGE_LIST
      const url = ispId ? `${baseUrl}?isp_id=${ispId}` : baseUrl
      this.$http
        .get(url)
        .then(res => {
          const rows = res.data && res.data.data ? res.data.data : []
          const normalized = (Array.isArray(rows) ? rows : []).map(pkg => ({
            id: pkg.id !== undefined ? pkg.id : pkg.value,
            package_name: pkg.package_name || pkg.label || pkg.name || pkg.text || null,
            price: pkg.price !== undefined ? pkg.price : (pkg.billing_price !== undefined ? pkg.billing_price : pkg.package_price),
          }))
          this.packageOptions = normalized.slice().sort((a, b) => {
            const nameA = (a.package_name || '').toString()
            const nameB = (b.package_name || '').toString()
            return nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true })
          })
        })
        .catch(() => {
          this.packageOptions = []
        })
    },
    getPackageById(packageId) {
      return (this.packageOptions || []).find(pkg => String(pkg.id) === String(packageId))
    },
    checkProfileMapping(packageId, routerId) {
      if (!packageId) return Promise.resolve(false)
      const ispId = this.getIspId()
      let url = `${process.env.VUE_APP_BASEURL + PACKAGE_PROFILE_MAPS}?package_id=${packageId}`
      if (ispId) {
        url += `&isp_id=${ispId}`
      }
      if (this.mappingMode === 'router' && routerId) {
        url += `&router_id=${routerId}`
      }
      return this.$http
        .get(url)
        .then(res => {
          const maps = res.data && res.data.data ? res.data.data : []
          return Array.isArray(maps) && maps.length > 0 && !!maps[0].profile_name
        })
        .catch(() => false)
    },
    showMappingMissingToast() {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Profile Mapping Missing',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
          text: 'No profile mapping found for this package/router. Please update mapping first.',
        },
      })
    },
    profileOptionsForRow(row) {
      if (!row) return []
      const routerId = row.router_id || row.billing_router_id
      const profiles = routerId ? (this.profilesByRouter[routerId] || []) : []
      const options = profiles.map(profile => ({ value: profile, text: profile }))
      const currentProfile = row.router_profile
      const expectedProfile = row.expected_profile

      if (currentProfile && !profiles.includes(currentProfile)) {
        options.unshift({ value: currentProfile, text: `${currentProfile} (current)` })
      }
      if (expectedProfile && !profiles.includes(expectedProfile) && expectedProfile !== currentProfile) {
        options.unshift({ value: expectedProfile, text: `${expectedProfile} (expected)` })
      }
      return options
    },
    ensureProfilesForRow(row) {
      if (!row) return
      const routerId = row.router_id || row.billing_router_id
      if (!routerId) return
      this.loadProfiles(routerId)
    },
    loadProfiles(routerId, force = false) {
      if (!routerId) return Promise.resolve([])
      if (!force && this.profilesByRouter[routerId]) {
        return Promise.resolve(this.profilesByRouter[routerId])
      }
      this.$set(this.profilesLoading, routerId, true)
      const url = `${process.env.VUE_APP_BASEURL}/api/get-ppp-profiles/${routerId}`
      return this.$http
        .get(url)
        .then(res => {
          if (res.data && res.data.code === 200) {
            this.$set(this.profilesByRouter, routerId, res.data.data.profiles || [])
          } else {
            this.$set(this.profilesByRouter, routerId, [])
          }
          return this.profilesByRouter[routerId]
        })
        .catch(() => {
          this.$set(this.profilesByRouter, routerId, [])
          return []
        })
        .finally(() => {
          this.$set(this.profilesLoading, routerId, false)
        })
    },
    handleRouterChange() {
      this.syncQuery()
    },
    resetFilters() {
      this.filters = {
        router: 'All',
        issue: 'All',
      }
      this.searchTerm = ''
      this.syncQuery()
    },
    clearSelection() {
      this.selectedRow = null
    },
    resetPage() {
      this.currentPage = 1
      this.$nextTick(() => {
        if (this.$refs.consistencyTable && this.$refs.consistencyTable.changePage) {
          this.$refs.consistencyTable.changePage(1)
        }
      })
    },
    syncQuery() {
      const query = {}
      if (this.filters.issue && this.filters.issue !== 'All') {
        query.issue = this.filters.issue
      }
      if (this.filters.router && this.filters.router !== 'All') {
        query.router_id = this.filters.router
      }

      if (JSON.stringify(query) !== JSON.stringify(this.$route.query || {})) {
        this.$router.replace({ name: 'isp-consistency-center', query })
      }
    },
    handlePageChange(value, props) {
      this.currentPage = value
      props.pageChanged({ currentPage: value })
    },
    onRowClick(params) {
      this.selectedRow = params.row || null
      if (this.selectedRow && this.selectedRow.type === 'profile_mismatch') {
        this.ensureProfilesForRow(this.selectedRow)
      }
    },
    rowClass(row) {
      if (!this.selectedRow) return ''
      const rowId = row.isp_user_name_id || row.user_name
      const selectedId = this.selectedRow.isp_user_name_id || this.selectedRow.user_name
      return rowId && selectedId && String(rowId) === String(selectedId) ? 'consistency-row-selected' : ''
    },
    rowActions(row) {
      if (!row) return []
      const hasUser = !!row.isp_user_name_id
      const hasRouter = !!(row.router_id || row.billing_router_id)
      const hasPackage = !!row.package_id
      const hasActualRouter = Array.isArray(row.actual_router_ids) && row.actual_router_ids.length > 0
      const actions = []

      switch (row.type) {
        case 'missing_in_router':
          if (hasUser) actions.push({ key: 'delete-billing', label: 'Remove', variant: 'outline-danger', tooltip: 'Delete from billing database' })
          break
        case 'missing_in_billing':
          actions.push({
            key: 'import-billing',
            label: 'Import',
            variant: 'outline-primary',
            tooltip: 'Create billing user from this router username',
          })
          actions.push({
            key: 'remove-router',
            label: 'Remove',
            variant: 'outline-danger',
            tooltip: 'Open router and remove PPP secret for this username',
            disabled: !hasRouter,
          })
          break
        case 'router_mismatch':
          actions.push({
            key: 'change-router',
            type: 'select',
            value: row.billing_router_id || row.router_id,
            options: this.routerSelectOptions,
            tooltip: 'Change billing router for this user',
            disabled: !this.routerSelectOptions.length,
          })
          break
        case 'status_mismatch':
          if (hasUser) {
            const billingLabel = row.billing_status ? 'Disable Billing' : 'Enable Billing'
            const billingTooltip = row.billing_status
              ? 'Disable user in billing system'
              : 'Enable user in billing system'
            actions.push({
              key: 'toggle-billing',
              label: billingLabel,
              variant: 'outline-primary',
              tooltip: billingTooltip,
            })
          }
          if (hasUser) {
            const routerLabel = row.router_disabled ? 'Enable Router' : 'Disable Router'
            const routerTooltip = row.router_disabled
              ? 'Enable user in MikroTik router'
              : 'Disable user in MikroTik router'
            actions.push({
              key: 'toggle-router',
              label: routerLabel,
              variant: 'outline-warning',
              tooltip: routerTooltip,
            })
          }
          break
        case 'profile_mismatch': {
          const profileOptions = this.profileOptionsForRow(row)
          actions.push({
            key: 'billing-package',
            type: 'select',
            value: row.package_id,
            options: this.packageSelectOptions,
            tooltip: 'Change billing package (updates expected profile)',
            disabled: !this.packageSelectOptions.length,
            selectClass: 'action-select-wide',
            labelText: 'Billing Package',
          })
          actions.push({
            key: 'router-profile',
            type: 'select',
            value: row.router_profile || row.expected_profile || null,
            options: profileOptions,
            tooltip: 'Change MikroTik PPP profile for this user',
            disabled: !profileOptions.length,
            selectClass: 'action-select-wide',
            labelText: 'Router Profile',
          })
          break
        }
        default:
          if (hasUser) actions.push({ key: 'open-user', label: 'User', variant: 'outline-primary', tooltip: 'Open billing user details' })
          if (hasRouter) actions.push({ key: 'open-router', label: 'Router', variant: 'outline-info', tooltip: 'Open router settings' })
          if (hasPackage) actions.push({ key: 'open-package', label: 'Package', variant: 'outline-primary', tooltip: 'Open package list' })
          actions.push({ key: 'copy-username', label: 'Copy', variant: 'outline-secondary', tooltip: 'Copy username' })
          break
      }

      return actions
    },
    handleRowAction(key, row, value = null) {
      if (!row) return
      switch (key) {
        case 'open-user':
          this.openUserFromRow(row)
          break
        case 'open-router':
          this.openRouterFromRow(row)
          break
        case 'open-actual-router':
          this.openActualRouterFromRow(row)
          break
        case 'open-package':
          this.openPackageFromRow()
          break
        case 'copy-username':
          this.copyText(row.user_name)
          break
        case 'copy-profile':
          this.copyText(row.expected_profile || row.meta_profile || row.router_profile)
          break
        case 'delete-billing':
          this.removeFromBilling(row)
          break
        case 'toggle-billing':
          this.toggleBillingStatus(row)
          break
        case 'toggle-router':
          this.toggleRouterStatus(row)
          break
        case 'open-user-create':
          this.openUserCreate()
          break
        case 'import-billing':
          this.importToBilling(row)
          break
        case 'remove-router':
          this.removeFromRouter(row)
          break
        case 'change-router':
          this.changeBillingRouter(row, value)
          break
        case 'billing-package':
          this.changeBillingPackage(row, value)
          break
        case 'router-profile':
          this.changeRouterProfile(row, value)
          break
        default:
          break
      }
    },
    openUserCreate() {
      this.$router.push({ name: 'isp-user-name-create' })
    },
    openPackage() {
      const link = this.$router.resolve({ name: 'isp-package-list' }).href
      window.open(link, '_blank', 'noopener')
    },
    openUserFromRow(row) {
      const link = this.userEditLink(row)
      if (link && link !== '#') {
        window.open(link, '_blank', 'noopener')
      }
    },
    openRouterFromRow(row) {
      const routerId = row.router_id || row.billing_router_id
      if (!routerId) return
      const link = this.$router.resolve({
        name: 'isp-router-edit',
        params: { routerId },
      }).href
      window.open(link, '_blank', 'noopener')
    },
    openActualRouterFromRow(row) {
      const actualId = Array.isArray(row.actual_router_ids) ? row.actual_router_ids[0] : null
      if (!actualId) return
      const link = this.$router.resolve({
        name: 'isp-router-edit',
        params: { routerId: actualId },
      }).href
      window.open(link, '_blank', 'noopener')
    },
    openPackageFromRow() {
      this.openPackage()
    },
    importToBilling(row) {
      if (!row) return
      this.copyText(row.user_name)
      const link = this.$router.resolve({ name: 'isp-user-name-create' }).href
      window.open(link, '_blank', 'noopener')
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Import to Billing',
          icon: 'UserPlusIcon',
          variant: 'info',
          text: `Username copied. Create billing user for "${row.user_name}".`,
        },
      })
    },
    removeFromRouter(row) {
      if (!row) return
      const routerId = row.router_id || row.billing_router_id
      if (!routerId) return
      this.copyText(row.user_name)
      const link = this.$router.resolve({
        name: 'isp-router-edit',
        params: { routerId },
      }).href
      window.open(link, '_blank', 'noopener')
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Remove from Router',
          icon: 'ServerIcon',
          variant: 'warning',
          text: `Router opened. Delete PPP secret "${row.user_name}" to remove from MikroTik.`,
        },
      })
    },
    changeBillingRouter(row, routerId) {
      if (!row || !routerId || !row.isp_user_name_id) return
      if (String(routerId) === String(row.router_id || row.billing_router_id)) return

      this.$http
        .get(process.env.VUE_APP_BASEURL + USERNAME_VIEW + row.isp_user_name_id)
        .then(res => {
          const data = res.data.data || {}
          const payload = {
            user_id: data.user_id,
            router_id: routerId,
            package_id: data.package_id,
            package_price: data.package_price,
            title: data.title,
            user_name: data.user_name,
            status: data.status_value,
            next_invoice_date: data.invoice_date,
            next_due_date: data.next_due_date,
            autogenerate: data.autogenerate,
            latitude: data.latitude,
            longitude: data.longitude,
            do_not_update_meta: true,
          }

          return this.$http.post(
            process.env.VUE_APP_BASEURL + USERNAME_UPDATE + row.isp_user_name_id,
            payload,
          )
        })
        .then(updateRes => {
          if (updateRes && checkRes(updateRes.data.code)) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Router Updated',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Billing router updated successfully.',
              },
            })
            this.fetchSummary({ force: true })
          } else if (updateRes) {
            this.toastMessage('warning', 'Router Update', updateRes)
          }
        })
        .catch(() => {})
    },
    changeBillingPackage(row, packageId) {
      if (!row || !packageId || !row.isp_user_name_id) return
      if (String(packageId) === String(row.package_id)) return

      const routerId = row.router_id || row.billing_router_id
      this.checkProfileMapping(packageId, routerId).then(isMapped => {
        if (!isMapped) {
          this.showMappingMissingToast()
          return
        }

        const selectedPackage = this.getPackageById(packageId)
        const packagePrice = selectedPackage ? selectedPackage.price : null

        this.$http
          .get(process.env.VUE_APP_BASEURL + USERNAME_VIEW + row.isp_user_name_id)
          .then(res => {
            const data = res.data.data || {}
            const payload = {
              user_id: data.user_id,
              router_id: data.router_id,
              package_id: packageId,
              package_price: packagePrice !== null ? packagePrice : data.package_price,
              title: data.title,
              user_name: data.user_name,
              status: data.status_value,
              next_invoice_date: data.invoice_date,
              next_due_date: data.next_due_date,
              autogenerate: data.autogenerate,
              latitude: data.latitude,
              longitude: data.longitude,
              do_not_update_meta: true,
            }

            return this.$http.post(
              process.env.VUE_APP_BASEURL + USERNAME_UPDATE + row.isp_user_name_id,
              payload,
            )
          })
          .then(updateRes => {
            if (updateRes && checkRes(updateRes.data.code)) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Billing Package',
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: 'Billing package updated successfully.',
                },
              })
              this.fetchSummary({ force: true })
            } else if (updateRes) {
              this.toastMessage('warning', 'Billing Package', updateRes)
            }
          })
          .catch(() => {})
      })
    },
    changeRouterProfile(row, profileName) {
      if (!row || !profileName || !row.isp_user_name_id) return
      if (profileName === row.router_profile) return

      const routerId = row.router_id || row.billing_router_id
      this.checkProfileMapping(row.package_id, routerId).then(isMapped => {
        if (!isMapped) {
          this.showMappingMissingToast()
          return
        }

        this.$http
          .post(process.env.VUE_APP_BASEURL + USERNAME_UPDATE_PPP_PROFILE, {
            user_id: row.isp_user_name_id,
            profile_name: profileName,
          })
          .then(res => {
            if (checkRes(res.data.code)) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Router Profile',
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: 'MikroTik profile updated successfully.',
                },
              })
              this.fetchSummary({ force: true })
            } else {
              this.toastMessage('warning', 'Router Profile', res)
            }
          })
          .catch(() => {})
      })
    },
    removeFromBilling(row = this.selectedRow) {
      if (!row || !row.isp_user_name_id) return
      this.$bvModal
        .msgBoxConfirm('Remove this user from billing?', {
          title: 'Confirm Remove',
          okVariant: 'danger',
          okTitle: 'Remove',
          cancelTitle: 'Cancel',
          centered: true,
        })
        .then(confirmed => {
          if (!confirmed) return
          return this.$http
            .post(process.env.VUE_APP_BASEURL + USERNAME_DELETE, {
              user_id: row.isp_user_name_id,
            })
            .then(res => {
              if (checkRes(res.data.code)) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'Billing User',
                    icon: 'CheckIcon',
                    variant: 'success',
                    text: 'User removed from billing successfully.',
                  },
                })
                this.fetchSummary({ force: true })
                this.clearSelection()
              } else {
                this.toastMessage('warning', 'Billing User', res)
              }
            })
            .catch(() => {})
        })
        .catch(() => {})
    },
    toggleBillingStatus(row = this.selectedRow) {
      if (!row || !row.isp_user_name_id) return
      const nextStatus = row.billing_status ? 0 : 1
      this.$http
        .get(process.env.VUE_APP_BASEURL + USERNAME_VIEW + row.isp_user_name_id)
        .then(res => {
          const data = res.data.data || {}
          const payload = {
            user_id: data.user_id,
            router_id: data.router_id,
            package_id: data.package_id,
            package_price: data.package_price,
            title: data.title,
            user_name: data.user_name,
            status: nextStatus,
            next_invoice_date: data.invoice_date,
            next_due_date: data.next_due_date,
            autogenerate: data.autogenerate,
            latitude: data.latitude,
            longitude: data.longitude,
            do_not_update_meta: true,
          }

          return this.$http.post(
            process.env.VUE_APP_BASEURL + USERNAME_UPDATE + row.isp_user_name_id,
            payload,
          )
        })
        .then(updateRes => {
          if (updateRes && checkRes(updateRes.data.code)) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Billing Status',
                icon: 'CheckIcon',
                variant: 'success',
                text: `Billing status ${nextStatus ? 'enabled' : 'disabled'} successfully.`,
              },
            })
            this.fetchSummary({ force: true })
          } else if (updateRes) {
            this.toastMessage('warning', 'Billing Status', updateRes)
          }
        })
        .catch(() => {})
    },
    toggleRouterStatus(row = this.selectedRow) {
      if (!row || !row.isp_user_name_id) return
      const desiredStatus = row.router_disabled ? 1 : 0
      this.$http
        .post(process.env.VUE_APP_BASEURL + USERNAME_TOGGLE_PPP, {
          user_id: row.isp_user_name_id,
          status: desiredStatus,
        })
        .then(res => {
          if (checkRes(res.data.code)) {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Router Status',
                icon: 'CheckIcon',
                variant: 'success',
                text: `Router status ${desiredStatus ? 'enabled' : 'disabled'} successfully.`,
              },
            })
            this.fetchSummary({ force: true })
          } else {
            this.toastMessage('warning', 'Router Status', res)
          }
        })
        .catch(() => {})
    },
    setRouterOptions(routers) {
      const options = [{ value: 'All', text: 'All Routers' }]
      ;(routers || []).forEach(router => {
        options.push({
          value: router.id,
          text: router.router_name,
        })
      })
      this.routerOptions = options
    },
    fetchSummary(options = {}) {
      this.isLoading = true
      const params = []
      if (this.filters.router && this.filters.router !== 'All') {
        params.push(`router_id=${this.filters.router}`)
      }
      if (options.force) {
        params.push('force=1')
      }
      const query = params.length ? `?${params.join('&')}` : ''
      this.$http
        .get(`${process.env.VUE_APP_BASEURL + CONSISTENCY_SUMMARY}${query}`)
        .then(res => {
          const data = res.data.data || {}
          this.summary = data.summary || this.summary
          this.items = this.decorateItems(data.items || [])
          this.routerErrors = data.router_errors || []
          this.checkedAt = data.checked_at || null
          this.mappingMode = data.mapping_mode || 'global'
          if (data.routers) {
            this.setRouterOptions(data.routers)
          }
        })
        .catch(() => {
          this.items = []
          this.routerErrors = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    exportCsv() {
      const rows = this.filteredItems
      const headers = ['Issue', 'Username', 'Router', 'Actual Router', 'Status', 'Profile', 'Package']
      const lines = [headers.join(',')]

      const escapeValue = value => {
        const text = value === null || value === undefined ? '' : String(value)
        if (text.includes('"') || text.includes(',') || text.includes('\n')) {
          return `"${text.replace(/\"/g, '""')}"`
        }
        return text
      }

      rows.forEach(row => {
        lines.push([
          escapeValue(row.issue_label),
          escapeValue(row.user_name),
          escapeValue(row.router_label),
          escapeValue(row.actual_router_label),
          escapeValue(row.status_label),
          escapeValue(row.profile_label),
          escapeValue(row.package_name),
        ].join(','))
      })

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.href = url
      link.download = `consistency-${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.consistency-card {
  .card-header {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .card-header-left {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .card-header-left .card-subtitle {
    margin-top: 0;
    line-height: 1.2;
  }

  .consistency-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .consistency-checked {
    font-size: 0.8rem;
    color: #6b7280;
    white-space: nowrap;
    margin-right: 0.5rem;
  }

  .consistency-kpi {
    background: #f8fafc;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.5rem;
    padding: 0.5rem 0.7rem;
    min-height: 62px;
  }

  .consistency-filter {
    background: #fbfbfc;
    border-color: #e6e8eb;
    padding: 0.75rem;
  }


  .consistency-filter .form-control,
  .consistency-filter .custom-select {
    height: 38px;
  }

  .consistency-clear-btn {
    height: 38px;
    border-radius: 0 8px 8px 0;
    box-shadow: 0 6px 12px rgba(79, 70, 229, 0.18);
  }

  .consistency-top-pagination {
    font-size: 0.85rem;
  }

  .consistency-page-select {
    width: 110px;
    min-width: 90px;
    height: 32px;
  }

  .consistency-actions .dropdown-toggle {
    padding: 0.25rem 0.6rem;
  }

  .consistency-row-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .consistency-row-actions .action-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .consistency-row-actions .action-label {
    font-size: 0.62rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  .consistency-row-actions .action-pill {
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    line-height: 1.1;
  }

  .consistency-row-actions .action-select {
    min-width: 160px;
    height: 28px;
    font-size: 0.72rem;
    border-radius: 999px;
    padding: 0 0.6rem;
  }

  .consistency-row-actions .action-select-wide {
    min-width: 190px;
  }

  .consistency-user-link {
    color: #4f46e5;
    text-decoration: none;
    cursor: pointer;
  }

  .consistency-user-link:hover {
    text-decoration: underline;
  }

  .consistency-row-selected {
    background: rgba(79, 70, 229, 0.06);
  }

  .consistency-skeleton {
    display: grid;
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .consistency-skeleton-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
    align-items: center;
  }

  .consistency-skeleton-block {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
    background-size: 200% 100%;
    animation: consistency-skeleton 1.4s ease-in-out infinite;
  }
}

@keyframes consistency-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
